
import React, { useEffect, useMemo, useRef, useState } from 'react';
import './VendingMachine.css';

// ============================================
// FLASK API
// ============================================

const API_URL = 'vending_products';

// ============================================
// ROSA_RELAY BLUETOOTH CONFIGURATION
// ============================================

const BT_SERVICE_UUID = '6e400001-b5a3-f393-e0a9-e50e24dcca9e';
const BT_CHAR_TX_UUID = '6e400002-b5a3-f393-e0a9-e50e24dcca9e';

async function connectBluetooth() {
  const device = await navigator.bluetooth.requestDevice({
    filters: [{ name: 'ROSA_RELAY' }],
    optionalServices: [BT_SERVICE_UUID],
  });

  const server = await device.gatt.connect();
  const service = await server.getPrimaryService(BT_SERVICE_UUID);
  const characteristic = await service.getCharacteristic(BT_CHAR_TX_UUID);

  return { device, characteristic };
}

async function sendBTCommand(characteristic, command) {
  const encoder = new TextEncoder();
  await characteristic.writeValue(encoder.encode(command));
}

// Convert Pocket ID to relay number.
// P01 -> 1, P02 -> 2 ... P08 -> 8.

const getRelayNumberFromPocket = (pocketId) => {
  const match = String(pocketId || '')
    .trim()
    .toUpperCase()
    .match(/^P0?([1-8])$/);

  return match ? Number(match[1]) : null;
};

// ============================================
// IMAGE URL HELPER
// ============================================

const productImageUrl = (image) => {
  if (!image) return '';

  if (/^https?:\/\//i.test(image)) return image;

  return `${process.env.PUBLIC_URL || ''}${image.startsWith('/') ? '' : '/'}${image}`;
};

// ============================================
// MULTIPLE PRODUCT IMAGES
// ============================================

const getProductImages = (product) => {
  let images = product.images || product.image_urls || [];

  if (typeof images === 'string') {
    try {
      images = JSON.parse(images);
    } catch {
      images = images.split(',').map((item) => item.trim()).filter(Boolean);
    }
  }

  if (!Array.isArray(images)) images = [];

  const result = images
    .map((item) =>
      typeof item === 'string'
        ? item
        : item?.image_url || item?.url || item?.image
    )
    .filter(Boolean);

  if (product.image && !result.includes(product.image)) {
    result.unshift(product.image);
  }

  return [...new Set(result)];
};

// ============================================
// CURRENCY FORMATTER
// ============================================

const money = (amount) =>
  new Intl.NumberFormat('en-IN', {
    style: 'currency',
    currency: 'INR',
    maximumFractionDigits: 0,
  }).format(Number(amount) || 0);

// ============================================
// PRODUCT IMAGE COMPONENT
// ============================================

function ProductImage({ image, name, className = '' }) {
  const [failed, setFailed] = useState(!image);

  useEffect(() => {
    setFailed(!image);
  }, [image]);

  return (
    <div className={`vm-image-wrap ${className}`}>
      {!failed ? (
        <img
          src={productImageUrl(image)}
          alt={name || 'Product'}
          loading="lazy"
          onError={() => setFailed(true)}
        />
      ) : (
        <div className="vm-image-placeholder">
          <span>IMAGE UNAVAILABLE</span>
        </div>
      )}
    </div>
  );
}

// ============================================
// MAIN VENDING MACHINE COMPONENT
// ============================================

export default function VendingMachine() {
  // ------------------------------------------
  // PRODUCTS
  // ------------------------------------------

  const [products, setProducts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [loadError, setLoadError] = useState('');

  // ------------------------------------------
  // SEARCH AND CATEGORY
  // ------------------------------------------

  const [search, setSearch] = useState('');
  const [category, setCategory] = useState('All Products');

  // ------------------------------------------
  // SELECTED PRODUCT
  // ------------------------------------------

  const [selectedProduct, setSelectedProduct] = useState(null);

  // ------------------------------------------
  // BASKET
  // ------------------------------------------

  const [cart, setCart] = useState([]);

  // ------------------------------------------
  // CHECKOUT
  // ------------------------------------------

  const [showCheckout, setShowCheckout] = useState(false);
  const [orderConfirmed, setOrderConfirmed] = useState(false);

  // ------------------------------------------
  // IMAGE PREVIEW
  // ------------------------------------------

  const [previewProduct, setPreviewProduct] = useState(null);
  const [isPreviewZoomed, setIsPreviewZoomed] = useState(false);

  // ------------------------------------------
  // BLUETOOTH STATE
  // ------------------------------------------

  const btCharacteristicRef = useRef(null);
  const btDeviceRef = useRef(null);
  const activeRelayRef = useRef(null);

  const [btConnected, setBtConnected] = useState(false);
  const [btConnecting, setBtConnecting] = useState(false);
  const [btDeviceName, setBtDeviceName] = useState('');
  const [relayStatus, setRelayStatus] = useState(
    'Connect to ROSA_RELAY to control the vending machine.'
  );

  // ==========================================
  // FETCH PRODUCTS FROM FLASK
  // ==========================================

  useEffect(() => {
    let isMounted = true;

    const fetchProducts = async () => {
      try {
        setLoading(true);
        setLoadError('');

        const response = await fetch(API_URL);

        if (!response.ok) {
          throw new Error(`API error: ${response.status}`);
        }

        const data = await response.json();

        if (!data.success || !Array.isArray(data.products)) {
          throw new Error(data.message || 'Invalid API response');
        }

        const formattedProducts = data.products.map((product) => {
          const image = product.image_url || product.image || '';

          const images = getProductImages({
            image,
            images: product.images,
            image_urls: product.image_urls,
          });

          return {
            id: product.product_id,
            name: product.product_name,
            category: product.category || 'Other',
            price: Number(product.price) || 0,
            description: product.description || '',
            pocketId: product.pocket_id || '',
            image: images[0] || image,
            images,
          };
        });

        if (isMounted) {
          setProducts(formattedProducts);
        }
      } catch (error) {
        console.error('Error fetching products:', error);

        if (isMounted) {
          setLoadError('Unable to load products. Please try again later.');
        }
      } finally {
        if (isMounted) {
          setLoading(false);
        }
      }
    };

    fetchProducts();

    return () => {
      isMounted = false;
    };
  }, []);

  // ==========================================
  // BLUETOOTH COMMAND FUNCTION
  // ==========================================

  const sendRelayCommand = async (relayNumber, action) => {
    const characteristic = btCharacteristicRef.current;

    if (!characteristic) {
      setRelayStatus('Please connect to ROSA_RELAY first.');
      return false;
    }

    const command = `${action}${relayNumber}`;

    try {
      await sendBTCommand(characteristic, command);

      console.log('Bluetooth command sent:', command);

      setRelayStatus(
        `Relay ${relayNumber}: ${action === 'ON' ? 'Activated' : 'Deactivated'}`
      );

      return true;
    } catch (error) {
      console.error('Bluetooth command error:', error);

      setRelayStatus(`Failed to send ${command}: ${error.message}`);

      return false;
    }
  };

  // ==========================================
  // ACTIVATE PRODUCT RELAY
  // ==========================================

  const activateRelayForProduct = async (product) => {
    const relayNumber = getRelayNumberFromPocket(product?.pocketId);

    if (!relayNumber) {
      setRelayStatus(
        `No valid relay assigned to Pocket ID: ${product?.pocketId || 'Not assigned'}`
      );
      return;
    }

    if (!btCharacteristicRef.current) {
      setRelayStatus(
        `Product selected. Connect ROSA_RELAY to activate Pocket ${product.pocketId}.`
      );
      return;
    }

    const previousRelay = activeRelayRef.current;

    // Switch off the previously activated relay.

    if (previousRelay && previousRelay !== relayNumber) {
      const offSuccess = await sendRelayCommand(previousRelay, 'OFF');

      if (!offSuccess) return;
    }

    // Avoid sending ON command repeatedly for the same relay.

    if (previousRelay === relayNumber) {
      setRelayStatus(`Pocket ${product.pocketId} is already activated.`);
      return;
    }

    const success = await sendRelayCommand(relayNumber, 'ON');

    if (success) {
      activeRelayRef.current = relayNumber;

      setRelayStatus(
        `Pocket ${product.pocketId} activated successfully. Relay ${relayNumber} ON.`
      );
    }
  };

  // ==========================================
  // BLUETOOTH CONNECT
  // ==========================================

  const handleBTConnect = async () => {
    if (!navigator.bluetooth) {
      setRelayStatus(
        'Web Bluetooth is not supported. Please use Chrome or Edge on a supported device.'
      );
      return;
    }

    try {
      setBtConnecting(true);
      setRelayStatus('Searching for ROSA_RELAY...');

      const { device, characteristic } = await connectBluetooth();

      btCharacteristicRef.current = characteristic;
      btDeviceRef.current = device;

      setBtConnected(true);
      setBtDeviceName(device.name || 'ROSA_RELAY');

      setRelayStatus(`Connected to ${device.name || 'ROSA_RELAY'}.`);

      device.addEventListener('gattserverdisconnected', () => {
        btCharacteristicRef.current = null;
        btDeviceRef.current = null;
        activeRelayRef.current = null;

        setBtConnected(false);
        setBtDeviceName('');

        setRelayStatus('Bluetooth device disconnected.');
      });

      // If a product was selected before connecting,
      // activate its relay after connection.

      if (selectedProduct) {
        await activateRelayForProduct(selectedProduct);
      }
    } catch (error) {
      console.error('Bluetooth connection error:', error);

      setRelayStatus(`Bluetooth connection failed: ${error.message}`);
    } finally {
      setBtConnecting(false);
    }
  };

  // ==========================================
  // BLUETOOTH DISCONNECT
  // ==========================================

  const handleBTDisconnect = async () => {
    try {
      const activeRelay = activeRelayRef.current;

      // Turn off the active relay before disconnecting.

      if (activeRelay && btCharacteristicRef.current) {
        await sendRelayCommand(activeRelay, 'OFF');
      }
    } catch (error) {
      console.error('Relay OFF error:', error);
    }

    const device = btDeviceRef.current;

    if (device?.gatt?.connected) {
      device.gatt.disconnect();
    }

    btCharacteristicRef.current = null;
    btDeviceRef.current = null;
    activeRelayRef.current = null;

    setBtConnected(false);
    setBtDeviceName('');
    setRelayStatus('Bluetooth disconnected.');
  };

  // ==========================================
  // DYNAMIC PRODUCT CATEGORIES
  // ==========================================

  const categories = useMemo(
    () => [
      'All Products',
      ...new Set(products.map((product) => product.category).filter(Boolean)),
    ],
    [products]
  );

  // ==========================================
  // FILTER PRODUCTS
  // ==========================================

  const filteredProducts = useMemo(() => {
    const term = search.trim().toLowerCase();

    return products.filter((product) => {
      const matchesCategory =
        category === 'All Products' || product.category === category;

      const searchableText =
        `${product.name} ${product.category} ${product.description} ${product.pocketId}`.toLowerCase();

      return matchesCategory && searchableText.includes(term);
    });
  }, [products, search, category]);

  // ==========================================
  // SELECT PRODUCT
  // ==========================================

  const selectProduct = (product) => {
    setSelectedProduct(product);

    setCart((current) => {
      const existing = current.find((item) => item.id === product.id);

      if (existing) return current;

      return [...current, { ...product, quantity: 1 }];
    });

    // Activate the relay assigned to this product.

    activateRelayForProduct(product);
  };

  // ==========================================
  // CHANGE PRODUCT QUANTITY
  // ==========================================

  const changeQuantity = (id, delta) => {
    setCart((current) =>
      current
        .map((item) =>
          item.id === id
            ? { ...item, quantity: item.quantity + delta }
            : item
        )
        .filter((item) => item.quantity > 0)
    );
  };

  // ==========================================
  // BASKET TOTALS
  // ==========================================

  const itemCount = cart.reduce((sum, item) => sum + item.quantity, 0);

  const total = cart.reduce(
    (sum, item) => sum + item.price * item.quantity,
    0
  );

  // ==========================================
  // CHECKOUT
  // ==========================================

  const openCheckout = () => {
    if (!cart.length) return;

    setOrderConfirmed(false);
    setShowCheckout(true);
  };

  const confirmDemoOrder = () => {
    if (!cart.length) return;

    setOrderConfirmed(true);
  };

  // ==========================================
  // FINISH ORDER
  // ==========================================

  const finishOrder = async () => {
    const activeRelay = activeRelayRef.current;

    if (activeRelay && btCharacteristicRef.current) {
      await sendRelayCommand(activeRelay, 'OFF');
    }

    activeRelayRef.current = null;

    setCart([]);
    setSelectedProduct(null);
    setShowCheckout(false);
    setOrderConfirmed(false);
    setPreviewProduct(null);
    setIsPreviewZoomed(false);
    setSearch('');
    setCategory('All Products');

    setRelayStatus('Order finished. Active relay switched OFF.');
  };

  // ==========================================
  // IMAGE PREVIEW
  // ==========================================

  const openBasketPreview = (product) => {
    setPreviewProduct(product);
    setIsPreviewZoomed(false);
  };

  // ==========================================
  // RENDER
  // ==========================================

  return (
    <div className="vm-app">
      {/* HEADER */}

      <header className="vm-header">
        <a
          className="vm-brand"
          href="/vending-machine"
          aria-label="Vending Machine home"
        >
          <div className="vm-brand-mark">V</div>

          <div>
            <div className="vm-brand-name">Vending Machine</div>
            <div className="vm-brand-subtitle">
              CRAFTED WITH CARE � CHOSEN BY YOU
            </div>
          </div>
        </a>

        <div className="vm-header-note">
          <span className="vm-live-dot" />
          SELF-SERVICE KIOSK
        </div>
      </header>

      {/* BLUETOOTH CONNECTION PANEL */}

      <section
        aria-label="Vending machine Bluetooth connection"
        style={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          flexWrap: 'wrap',
          gap: '12px',
          padding: '12px 18px',
          background: '#fffaf4',
          borderBottom: '1px solid #eadbca',
        }}
      >
        <span
          style={{
            padding: '7px 14px',
            borderRadius: '20px',
            background: btConnected ? '#e7f5e9' : '#fff0e8',
            color: btConnected ? '#236b36' : '#96502d',
            border: `1px solid ${btConnected ? '#9acda5' : '#e6b69e'}`,
            fontSize: '13px',
            fontWeight: 700,
          }}
        >
          {btConnected
            ? `Bluetooth: ${btDeviceName} connected`
            : 'Bluetooth: Disconnected'}
        </span>

        {!btConnected ? (
          <button
            type="button"
            onClick={handleBTConnect}
            disabled={btConnecting}
            style={{
              border: 0,
              borderRadius: '20px',
              padding: '9px 18px',
              background: btConnecting ? '#b8a18d' : '#76513d',
              color: '#fff',
              fontWeight: 700,
              cursor: btConnecting ? 'wait' : 'pointer',
            }}
          >
            {btConnecting ? 'Connecting...' : 'Connect ROSA_RELAY'}
          </button>
        ) : (
          <button
            type="button"
            onClick={handleBTDisconnect}
            style={{
              border: 0,
              borderRadius: '20px',
              padding: '9px 18px',
              background: '#9a4938',
              color: '#fff',
              fontWeight: 700,
              cursor: 'pointer',
            }}
          >
            Disconnect
          </button>
        )}

        <span
          role="status"
          style={{
            flexBasis: '100%',
            textAlign: 'center',
            color: '#76513d',
            fontSize: '13px',
          }}
        >
          {relayStatus}
        </span>
      </section>

      {/* HERO SECTION */}

      <section className="vm-hero">
        <div className="vm-hero-copy">
          <span className="vm-eyebrow">A LITTLE SOMETHING HANDMADE</span>

          <h1>
            Discover something
            <br />
            <em>beautiful.</em>
          </h1>

          <p>
            Explore thoughtfully crafted keepsakes, home decor and gifts from
            local artisans.
          </p>

          <div className="vm-hero-pills">
            <span>Artisan picks</span>
            <span>Made with care</span>
          </div>

          <button
            type="button"
            className="vm-hero-button"
            onClick={() =>
              document.getElementById('vm-collection')?.scrollIntoView({
                behavior: 'smooth',
              })
            }
          >
            Explore collection
          </button>
        </div>

        <div className="vm-hero-art">
          <div className="vm-hero-circle" />

          {products[1] && (
            <div className="vm-hero-product vm-hero-product-one">
              <ProductImage
                image={products[1].image}
                name={products[1].name}
              />
            </div>
          )}

          {products[0] && (
            <div className="vm-hero-product vm-hero-product-two">
              <ProductImage
                image={products[0].image}
                name={products[0].name}
              />
            </div>
          )}

          <span className="vm-hero-star vm-hero-star-one">?</span>
          <span className="vm-hero-star vm-hero-star-two">?</span>
        </div>
      </section>

      {/* MAIN COLLECTION */}

      <main className="vm-main" id="vm-collection">
        <div className="vm-section-heading">
          <div>
            <span className="vm-eyebrow">THE COLLECTION</span>
            <h2>Choose your product</h2>
            <p>
              Select a product image to view it larger and add it to your
              basket.
            </p>
          </div>

          <label className="vm-search">
            <span aria-hidden="true">?</span>

            <input
              value={search}
              onChange={(event) => setSearch(event.target.value)}
              placeholder="Search products..."
              aria-label="Search products"
            />

            {search && (
              <button
                type="button"
                onClick={() => setSearch('')}
                aria-label="Clear search"
              >
                �
              </button>
            )}
          </label>
        </div>

        {/* CATEGORY FILTER */}

        <div className="vm-category-row" aria-label="Product categories">
          {categories.map((item) => (
            <button
              type="button"
              key={item}
              className={`vm-category ${category === item ? 'active' : ''}`}
              onClick={() => setCategory(item)}
            >
              {item}
            </button>
          ))}
        </div>

        <div className="vm-content-layout">
          {/* LEFT: PRODUCT IMAGE LIST */}

          <section className="vm-products-section" aria-label="Products">
            <div className="vm-results-line">
              <span>
                {loading
                  ? 'Loading products...'
                  : `${filteredProducts.length} products`}
              </span>

              <span>Tap an image to select</span>
            </div>

            <div className="vm-products-grid">
              {loading && (
                <div className="vm-empty">
                  <h3>Loading products...</h3>
                  <p>Please wait while we load the collection.</p>
                </div>
              )}

              {!loading && loadError && (
                <div className="vm-empty">
                  <h3>Products unavailable</h3>
                  <p>{loadError}</p>

                  <button
                    type="button"
                    onClick={() => window.location.reload()}
                  >
                    Try again
                  </button>
                </div>
              )}

              {!loading &&
                !loadError &&
                filteredProducts.map((product) => (
                  <button
                    type="button"
                    key={product.id}
                    className={`vm-product-image-button ${
                      selectedProduct?.id === product.id ? 'selected' : ''
                    }`}
                    onClick={() => selectProduct(product)}
                    aria-label={`Select ${product.name}`}
                    aria-pressed={selectedProduct?.id === product.id}
                  >
                    <ProductImage
                      image={product.image}
                      name={product.name}
                      className="vm-card-image"
                    />
                  </button>
                ))}

              {!loading && !loadError && filteredProducts.length === 0 && (
                <div className="vm-empty">
                  <h3>No products found</h3>
                  <p>Try another product name or category.</p>

                  <button
                    type="button"
                    onClick={() => {
                      setSearch('');
                      setCategory('All Products');
                    }}
                  >
                    Show all products
                  </button>
                </div>
              )}
            </div>
          </section>

          {/* MIDDLE: LARGE SELECTED IMAGE */}

          <section
            className="vm-large-product-panel"
            aria-label="Selected product image"
          >
            {selectedProduct ? (
              <div className="vm-large-product-image">
                <ProductImage
                  image={selectedProduct.image}
                  name={selectedProduct.name}
                />
              </div>
            ) : (
              <div className="vm-large-image-empty">
                <span>SELECT A PRODUCT</span>
              </div>
            )}
          </section>

          {/* RIGHT: BASKET */}

          <aside className="vm-cart-panel">
            <div className="vm-cart-title">
              <div>
                <span className="vm-eyebrow">YOUR SELECTION</span>
                <h2>My basket</h2>
              </div>

              <span className="vm-cart-count">{itemCount}</span>
            </div>

            {cart.length === 0 ? (
              <div className="vm-cart-empty">
                <div className="vm-empty-basket">?</div>
                <strong>Your basket is waiting</strong>
                <p>Choose a product image to add it to your basket.</p>
              </div>
            ) : (
              <div className="vm-cart-items">
                {cart.map((item) => (
                  <div className="vm-cart-item" key={item.id}>
                    <button
                      type="button"
                      className="vm-cart-image-preview"
                      onClick={() => openBasketPreview(item)}
                      aria-label={`Preview ${item.name}`}
                    >
                      <ProductImage
                        image={item.image}
                        name={item.name}
                        className="vm-cart-product-image"
                      />
                    </button>

                    <div className="vm-cart-item-main">
                      <strong>{item.name}</strong>
                      <span>{money(item.price)} each</span>
                      <small>
                        Pocket ID: {item.pocketId || 'Not assigned'}
                      </small>

                      <div className="vm-qty-controls">
                        <button
                          type="button"
                          onClick={() => changeQuantity(item.id, -1)}
                          aria-label={`Remove one ${item.name}`}
                        >
                          -
                        </button>

                        <span>{item.quantity}</span>

                        <button
                          type="button"
                          onClick={() => changeQuantity(item.id, 1)}
                          aria-label={`Add one ${item.name}`}
                        >
                          +
                        </button>
                      </div>
                    </div>

                    <strong className="vm-line-total">
                      {money(item.price * item.quantity)}
                    </strong>
                  </div>
                ))}
              </div>
            )}

            <div className="vm-cart-summary">
              <div className="vm-summary-line">
                <span>Items</span>
                <span>{itemCount}</span>
              </div>

              <div className="vm-summary-line">
                <span>Delivery</span>
                <span>At kiosk</span>
              </div>

              <div className="vm-total-row">
                <strong>Total Amount</strong>
                <strong>{money(total)}</strong>
              </div>

              <button
                type="button"
                className="vm-checkout-button"
                disabled={!cart.length}
                onClick={openCheckout}
              >
                Continue with UPI
              </button>

              <p className="vm-secure-note">Demo payment mode</p>
            </div>
          </aside>
        </div>
      </main>

      {/* FOOTER */}

      <footer className="vm-footer">
        <span>VENDING MACHINE</span>
        <span>Celebrating the art of handmade.</span>
        <span>DEMO STOREFRONT</span>
      </footer>

      {/* CHECKOUT MODAL */}

      {showCheckout && (
        <div
          className="vm-modal-backdrop"
          onMouseDown={(event) => {
            if (event.target === event.currentTarget) {
              setShowCheckout(false);
            }
          }}
        >
          <section
            className="vm-checkout-modal"
            role="dialog"
            aria-modal="true"
            aria-labelledby="vm-checkout-title"
          >
            <button
              type="button"
              className="vm-modal-close"
              onClick={() => setShowCheckout(false)}
              aria-label="Close checkout"
            >
              �
            </button>

            {!orderConfirmed ? (
              <>
                <span className="vm-eyebrow">SECURE CHECKOUT</span>

                <h2 id="vm-checkout-title">UPI Payment</h2>

                <p className="vm-modal-intro">
                  Review your selected products and total amount.
                </p>

                <div className="vm-modal-order-items">
                  {cart.map((item) => (
                    <div key={item.id}>
                      <span>
                        {item.name} � {item.quantity}
                      </span>

                      <strong>
                        {money(item.price * item.quantity)}
                      </strong>
                    </div>
                  ))}
                </div>

                <div className="vm-modal-total">
                  <span>Total Amount</span>
                  <strong>{money(total)}</strong>
                </div>

                <p className="vm-demo-warning">
                  Demo only. No real payment is initiated or collected.
                </p>

                <button
                  type="button"
                  className="vm-checkout-button vm-pay-button"
                  onClick={confirmDemoOrder}
                >
                  Confirm Demo Order
                </button>
              </>
            ) : (
              <div className="vm-demo-success" role="status">
                <div className="vm-success-icon">?</div>

                <span className="vm-eyebrow">ORDER CONFIRMED</span>

                <h2 id="vm-checkout-title">Thank you!</h2>

                <p>
                  Your demo order for <strong>{money(total)}</strong> has been
                  prepared. No payment was collected.
                </p>

                <button
                  type="button"
                  className="vm-checkout-button"
                  onClick={finishOrder}
                >
                  Finish Order
                </button>
              </div>
            )}
          </section>
        </div>
      )}

      {/* BASKET IMAGE PREVIEW */}

      {previewProduct && (
        <div
          className="vm-preview-backdrop"
          onMouseDown={(event) => {
            if (event.target === event.currentTarget) {
              setPreviewProduct(null);
              setIsPreviewZoomed(false);
            }
          }}
        >
          <div className="vm-preview-modal">
            <button
              type="button"
              className="vm-preview-close"
              onClick={() => {
                setPreviewProduct(null);
                setIsPreviewZoomed(false);
              }}
              aria-label="Close image preview"
            >
              �
            </button>

            <div className="vm-preview-heading">
              <strong>{previewProduct.name}</strong>
              <span>{money(previewProduct.price)}</span>
            </div>

            <div className="vm-preview-image-container">
              <img
                src={productImageUrl(previewProduct.image)}
                alt={previewProduct.name}
                className={isPreviewZoomed ? 'zoomed' : ''}
                onClick={() =>
                  setIsPreviewZoomed((current) => !current)
                }
                onError={(event) => {
                  event.currentTarget.style.display = 'none';
                }}
              />
            </div>

            <p className="vm-preview-hint">
              Click the image to zoom in or out
            </p>
          </div>
        </div>
      )}
    </div>
  );
}
